import type { LoginUser, TeamMember, TeamRelationship } from '../types/Team';

export const RELATIONSHIP_LABELS: Record<TeamRelationship, string> = {
  'bride-side': 'Bride side',
  'groom-side': 'Groom side',
  vendor: 'Vendor',
  neutral: 'Neutral',
};

export const RELATIONSHIP_OPTIONS: Array<{ value: TeamRelationship; label: string }> =
  (Object.keys(RELATIONSHIP_LABELS) as TeamRelationship[]).map(value => ({ value, label: RELATIONSHIP_LABELS[value] }));

export type SideFilter = 'all' | TeamRelationship;
export const SIDE_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All sides' },
  ...RELATIONSHIP_OPTIONS,
];

export interface TeamFilters {
  side: SideFilter;
  showDeactivated: boolean;
  search: string;
}

export const DEFAULT_TEAM_FILTERS: TeamFilters = { side: 'all', showDeactivated: false, search: '' };

/** The people shown for a filter state. Returns a new array; the input is not touched. */
export function filterTeam(members: readonly TeamMember[], filters: TeamFilters): TeamMember[] {
  const q = filters.search.trim().toLowerCase();
  return members.filter(m => {
    if (!filters.showDeactivated && m.is_active === false) return false;
    if (filters.side !== 'all' && m.relationship !== filters.side) return false;
    if (!q) return true;
    return [m.name, m.role ?? '', ...(m.teams ?? [])].some(text => text.toLowerCase().includes(q));
  });
}

export interface TeamMetrics {
  total: number;
  active: number;
  deactivated: number;
  withLogin: number;
}

/** Headline numbers for the cards. `logins` are the workspace's accounts. */
export function teamMetrics(members: readonly TeamMember[], logins: readonly LoginUser[]): TeamMetrics {
  const emails = new Set(logins.map(l => normaliseEmail(l.email)).filter(Boolean));
  return {
    total: members.length,
    active: members.filter(m => m.is_active !== false).length,
    deactivated: members.filter(m => m.is_active === false).length,
    withLogin: members.filter(m => emails.has(normaliseEmail(m.email))).length,
  };
}

export const normaliseEmail = (email: string | null | undefined): string => (email ?? '').trim().toLowerCase();

/** The login that shares a person's e-mail, if any. */
export function loginFor(member: TeamMember, logins: readonly LoginUser[]): LoginUser | undefined {
  const email = normaliseEmail(member.email);
  return email ? logins.find(l => normaliseEmail(l.email) === email) : undefined;
}

export interface LoginRow {
  login: LoginUser;
  /** The team profile that shares this login's e-mail, or null when the login has none. */
  member: TeamMember | null;
}

/** Every login of the workspace, paired with its team profile when one exists (the merged list). */
export function mergeLogins(logins: readonly LoginUser[], members: readonly TeamMember[]): LoginRow[] {
  return logins.map(login => {
    const email = normaliseEmail(login.email);
    return { login, member: (email && members.find(m => normaliseEmail(m.email) === email)) || null };
  });
}

/** CSV text of the shown people (contact details only when asked for by an admin export). */
export function teamCsvRows(members: readonly TeamMember[]): unknown[][] {
  return members.map(m => [
    m.name, m.role ?? '', m.relationship ? RELATIONSHIP_LABELS[m.relationship] : '',
    (m.teams ?? []).join('; '), m.phone ?? '', m.email ?? '',
  ]);
}
export const TEAM_CSV_HEADERS = ['Name', 'Role', 'Side', 'Teams', 'Phone', 'Email'];
